import {
  businessReportsApi,
  type HospitalCashFlowMomResponse,
  type HospitalCashFlowResponse,
} from '@/api/businessReports';
import { CashFlowBreakdownHover } from '@/components/business-reports/CashFlowBreakdownHover';
import { CashFlowEditableCell } from '@/components/business-reports/CashFlowEditableCell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  isCashFlowLineEditable,
  isCashFlowLineVisible,
} from '@/constants/cashFlowLines';
import { isCfBreakdownLineId } from '@/lib/cashFlowBreakdownLines';
import { IndianRupee } from 'lucide-react';

const EMPHASIZE = ['CF4', 'CF12', 'CF23', 'CF24', 'CF28', 'CF34'];

type CashFlowReportPanelProps = {
  mode: 'MTD' | 'YTD' | 'MOM';
  report:
    | HospitalCashFlowResponse
    | HospitalCashFlowMomResponse
    | undefined;
  loading: boolean;
  error: boolean;
  queryError: unknown;
  queryKey: unknown[];
  hospitalId: string;
  editable: boolean;
  fyStartYear?: number;
};

export function CashFlowReportPanel({
  mode,
  report,
  loading,
  error,
  queryError,
  queryKey,
  hospitalId,
  editable,
  fyStartYear,
}: CashFlowReportPanelProps) {
  const hospitalParam = hospitalId === 'all' ? 'all' : hospitalId;
  const isConsolidated = hospitalId === 'all';

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IndianRupee className="w-5 h-5 text-emerald-600" />
          Cash Flow Statement{mode === 'MOM' ? ' — Month on Month' : ''}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Indian FY (Apr–Mar). Cash basis: collections by payment date; outflows by expense paid date.
          {isConsolidated ? (
            <>
              {' '}
              Opening/closing balances (CF1–CF4, CF25–CF28) are consolidated only — double-click to
              override; right-click amber cells to revert.
            </>
          ) : (
            <>
              {' '}
              Hospital-wise view shows cash movement only; opening/closing balances appear in
              consolidated scope.
            </>
          )}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500">
            Loading Cash Flow…
            {mode === 'MOM' ? ' MoM builds each month sequentially — may take up to a minute.' : ''}
          </div>
        ) : error ? (
          <div className="text-sm text-destructive">
            {(queryError as { response?: { data?: { message?: string } } })?.response?.data?.message ||
              (queryError instanceof Error ? queryError.message : null) ||
              'Failed to load Cash Flow.'}
          </div>
        ) : !report ? (
          <div className="text-sm text-gray-500">No Cash Flow data for this selection.</div>
        ) : 'view' in report && report.view === 'MOM' ? (
          (() => {
            const mom = report as HospitalCashFlowMomResponse;
            const template = (mom.months[0]?.lines ?? []).filter((row) =>
              isCashFlowLineVisible(row.id, isConsolidated)
            );
            return (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left sticky left-0 bg-gray-50 z-10">ID</th>
                        <th className="p-2 text-left sticky left-10 bg-gray-50 z-10 min-w-[200px]">
                          Metric
                        </th>
                        {mom.months.map((m) => (
                          <th key={m.month} className="p-2 text-right whitespace-nowrap">
                            {m.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {template.map((row, idx) => {
                        const prev = idx > 0 ? template[idx - 1] : null;
                        const sectionBreak = prev && prev.section !== row.section;
                        return (
                          <tr
                            key={row.id}
                            className={`border-t ${sectionBreak ? 'border-t-2 border-t-slate-300' : ''} ${
                              EMPHASIZE.includes(row.id) ? 'bg-slate-50 font-semibold' : ''
                            }`}
                          >
                            <td className="p-2 text-muted-foreground sticky left-0 bg-inherit z-10">
                              {row.id}
                            </td>
                            <td className="p-2 sticky left-10 bg-inherit z-10">
                              {isCfBreakdownLineId(row.id) ? (
                                <CashFlowBreakdownHover
                                  lineId={row.id}
                                  label={row.label}
                                  consolidated={isConsolidated}
                                  periodMode="MTD"
                                  periodKey=""
                                  hospitalId={hospitalParam}
                                />
                              ) : (
                                row.label
                              )}
                            </td>
                            {mom.months.map((m) => {
                              const cell = m.lines.find((l) => l.id === row.id);
                              const editableCell = (
                                <CashFlowEditableCell
                                  cell={cell}
                                  rowFormat={row.format}
                                  periodKey={m.month}
                                  periodMode="MTD"
                                  hospitalId={hospitalParam}
                                  queryKey={queryKey}
                                  editable={isCashFlowLineEditable(row.id, editable, isConsolidated)}
                                />
                              );
                              return (
                                <td
                                  key={`${m.month}-${row.id}`}
                                  className="p-2 text-right whitespace-nowrap"
                                >
                                  {isCfBreakdownLineId(row.id) ? (
                                    <CashFlowBreakdownHover
                                      lineId={row.id}
                                      label={row.label}
                                      consolidated={isConsolidated}
                                      periodMode="MTD"
                                      periodKey={m.month}
                                      hospitalId={hospitalParam}
                                    >
                                      {editableCell}
                                    </CashFlowBreakdownHover>
                                  ) : (
                                    editableCell
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Drill-down rows (dotted metric names) show cash sources/categories and line items on
                  hover. Click a drill-down value to download the calculation as CSV. Amber cells are
                  editable overrides (double-click to edit; right-click to revert).
                </p>
              </div>
            );
          })()
        ) : (
          (() => {
            const single = report as HospitalCashFlowResponse;
            const periodKey = single.period.periodKey;
            const periodMode = single.period.mode;
            const visibleLines = single.lines.filter((row) =>
              isCashFlowLineVisible(row.id, isConsolidated)
            );
            return (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">ID</th>
                        <th className="p-2 text-left">Metric</th>
                        <th className="p-2 text-right">Value</th>
                        <th className="p-2 text-left hidden lg:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLines.map((row, idx) => {
                        const prev = idx > 0 ? visibleLines[idx - 1] : null;
                        const sectionBreak = prev && prev.section !== row.section;
                        const editableCell = (
                          <CashFlowEditableCell
                            cell={row}
                            rowFormat={row.format}
                            periodKey={periodKey}
                            periodMode={periodMode}
                            hospitalId={hospitalParam}
                            queryKey={queryKey}
                            editable={isCashFlowLineEditable(row.id, editable, isConsolidated)}
                          />
                        );
                        return (
                          <tr
                            key={row.id}
                            className={`border-t ${sectionBreak ? 'border-t-2 border-t-slate-300' : ''} ${
                              EMPHASIZE.includes(row.id) ? 'bg-slate-50 font-semibold' : ''
                            }`}
                          >
                            <td className="p-2 text-muted-foreground">{row.id}</td>
                            <td className="p-2">
                              {isCfBreakdownLineId(row.id) ? (
                                <CashFlowBreakdownHover
                                  lineId={row.id}
                                  label={row.label}
                                  consolidated={isConsolidated}
                                  periodMode={periodMode}
                                  periodKey={periodKey}
                                  fyStartYear={
                                    periodMode === 'YTD' ? fyStartYear : undefined
                                  }
                                  hospitalId={hospitalParam}
                                />
                              ) : (
                                <div>{row.label}</div>
                              )}
                            </td>
                            <td className="p-2 text-right whitespace-nowrap">
                              {isCfBreakdownLineId(row.id) ? (
                                <CashFlowBreakdownHover
                                  lineId={row.id}
                                  label={row.label}
                                  consolidated={isConsolidated}
                                  periodMode={periodMode}
                                  periodKey={periodKey}
                                  fyStartYear={
                                    periodMode === 'YTD' ? fyStartYear : undefined
                                  }
                                  hospitalId={hospitalParam}
                                >
                                  {editableCell}
                                </CashFlowBreakdownHover>
                              ) : (
                                editableCell
                              )}
                            </td>
                            <td className="p-2 text-xs text-muted-foreground hidden lg:table-cell">
                              {row.note ?? row.formula ?? ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Drill-down rows (dotted metric names) show cash sources/categories and line items on
                  hover. Click a drill-down value to download the calculation as CSV. Amber cells are
                  editable overrides (double-click to edit; right-click to revert).
                </p>
              </div>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}

export async function downloadCashFlowCsv(opts: {
  mode: 'MTD' | 'YTD' | 'MOM';
  month?: string;
  fy?: number;
  hospitalId?: string | null;
}) {
  const resp = await businessReportsApi.hospitalCashFlowExport(opts);
  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CashFlow-${opts.mode}-${
    opts.mode === 'MTD' ? opts.month : `FY${opts.fy}`
  }.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
